import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getSubscription, getPlans, createCheckout, createPortalSession, getCompany, updateCompany,
  getStripeSubscription, getChangePreview, changePlan, cancelPendingChange, getReferral, getCampaign,
} from '../api/client'
import { Check, Zap, Star, Loader2, ExternalLink, X, Camera, ArrowRightLeft, Info, Gift, Copy } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'

const PLAN_COLORS: Record<string, string> = {
  free:    'bg-gray-100 text-gray-700 border-gray-200',
  starter: 'bg-blue-50 text-blue-700 border-blue-200',
  pro:     'bg-brand-50 text-brand-700 border-brand-200',
}

interface BillingForm {
  name: string
  tax_number: string
  address: string
  email: string
}

function BillingModal({
  onConfirm,
  onClose,
  loading,
  initial,
}: {
  onConfirm: (data: BillingForm) => void
  onClose: () => void
  loading: boolean
  initial: BillingForm
}) {
  const [form, setForm] = useState<BillingForm>(initial)
  const needsTax = form.name.trim().length > 0

  function set(k: keyof BillingForm) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (needsTax && !form.tax_number.trim()) {
      toast.error('Cégnév megadása esetén az adószám kötelező')
      return
    }
    onConfirm(form)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X size={20} />
        </button>

        <h2 className="text-lg font-bold mb-1" style={{ color: '#1E2D27' }}>Számlázási adatok</h2>
        <p className="text-sm text-gray-500 mb-5">
          Ezek az adatok kerülnek a számlára. Utólag a Beállításokban is módosíthatók.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Cégnév / vállalkozás neve</label>
            <input className="input" placeholder="pl. Napsugár Cukrászda Kft." value={form.name} onChange={set('name')} />
          </div>

          <div>
            <label className="label">
              Adószám
              {needsTax && <span className="text-red-500 ml-1">*</span>}
              {!needsTax && <span className="text-gray-400 font-normal text-xs ml-1">— opcionális magánszemélynek</span>}
            </label>
            <input
              className="input"
              placeholder="12345678-1-23"
              value={form.tax_number}
              onChange={set('tax_number')}
              required={needsTax}
            />
          </div>

          <div>
            <label className="label">Számlázási cím</label>
            <input className="input" placeholder="1111 Budapest, Példa u. 1." value={form.address} onChange={set('address')} />
          </div>

          <div>
            <label className="label">E-mail (számla értesítőhöz)</label>
            <input className="input" type="email" placeholder="nev@email.hu" value={form.email} onChange={set('email')} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 mt-2"
            style={{ background: '#5BB8A0', color: '#fff' }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Tovább a fizetéshez
          </button>
        </form>
      </div>
    </div>
  )
}

const PRICE_KEY_LABELS: Record<string, string> = {
  starter_monthly: 'Alap havi',
  starter_annual:  'Alap éves',
  pro_monthly:     'Pro havi',
  pro_annual:      'Pro éves',
}

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('hu', { year: 'numeric', month: 'long', day: 'numeric' }) : ''
const fmtHuf = (n: number) => `${Math.round(n).toLocaleString('hu')} Ft`

function ChangePlanModal({ priceKey, onClose, onDone }: { priceKey: string; onClose: () => void; onDone: () => void }) {
  const [submitting, setSubmitting] = useState(false)
  const { data: preview, isLoading, error } = useQuery({
    queryKey: ['change-preview', priceKey],
    queryFn: () => getChangePreview(priceKey).then(r => r.data),
    staleTime: 0,
  })

  async function confirm() {
    setSubmitting(true)
    try {
      const res = await changePlan(priceKey, preview?.proration_date)
      if (res.data.status === 'payment_required' && res.data.hosted_invoice_url) {
        // Banki megerősítés (3D Secure) kell — a Stripe számla-oldalán fizethető ki
        window.location.href = res.data.hosted_invoice_url
        return
      }
      toast.success(res.data.status === 'scheduled'
        ? `Rendben, ${fmtDate(res.data.effective_at)}-tól ${PRICE_KEY_LABELS[priceKey]} csomagra váltunk.`
        : `Sikeres váltás — mostantól ${PRICE_KEY_LABELS[priceKey]} csomagon vagy.`)
      onDone()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'A csomagváltás nem sikerült')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X size={20} />
        </button>
        <h2 className="text-lg font-bold mb-4" style={{ color: '#1E2D27' }}>
          Váltás: {PRICE_KEY_LABELS[priceKey]}
        </h2>

        {isLoading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
        {error && <p className="text-sm text-red-600">{(error as any)?.response?.data?.detail || 'Nem sikerült kiszámolni a váltás díját.'}</p>}

        {preview?.direction === 'upgrade' && (
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              A váltás <strong>azonnal</strong> életbe lép. A jelenlegi {PRICE_KEY_LABELS[preview.current_price_key]} csomagod
              fel nem használt napjait jóváírjuk, így csak a különbözetet kell kifizetned.
            </p>
            <div className="rounded-xl bg-gray-50 p-4 space-y-1.5">
              {preview.credit > 0 && (
                <div className="flex justify-between"><span>Jóváírás (fel nem használt napok)</span><span>−{fmtHuf(preview.credit)}</span></div>
              )}
              {preview.campaign_discount > 0 && (
                <div className="flex justify-between" style={{ color: '#3f8f7a' }}>
                  <span>🎄 Karácsonyi akció (−20%)</span><span>−{fmtHuf(preview.campaign_discount)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-gray-900 text-base pt-1">
                <span>Most fizetendő (bruttó)</span><span>{fmtHuf(preview.amount_due)}</span>
              </div>
              {preview.total < 0 && (
                <p className="text-xs text-gray-500">A fennmaradó {fmtHuf(-preview.total)} jóváírást a következő számláidból vonjuk le.</p>
              )}
            </div>
            <p>Következő megújítás: <strong>{fmtDate(preview.new_period_end)}</strong></p>
            <p className="text-xs text-gray-400">Az összeget a mentett bankkártyádról vonjuk le, a számlát e-mailben küldjük.</p>
          </div>
        )}

        {preview?.direction === 'downgrade' && (
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              A jelenlegi <strong>{PRICE_KEY_LABELS[preview.current_price_key]}</strong> csomagodat már kifizetted, ezért
              <strong> {fmtDate(preview.effective_at)}-ig</strong> megmarad minden funkcióval.
            </p>
            <p>
              Ezután automatikusan <strong>{PRICE_KEY_LABELS[preview.new_price_key]}</strong> csomagon folytatod, és onnantól
              annak a díját vonjuk le. Most nem fizetsz semmit, és a már kifizetett időszakra visszatérítés nem jár.
            </p>
            <p className="text-xs text-gray-400">A váltást a megadott napig bármikor visszavonhatod ezen az oldalon.</p>
          </div>
        )}

        {preview && (
          <button
            onClick={confirm}
            disabled={submitting}
            className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 mt-5 disabled:opacity-60"
            style={{ background: '#5BB8A0', color: '#fff' }}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
            {preview.direction === 'upgrade' ? `Váltás és ${fmtHuf(preview.amount_due)} fizetése` : 'Váltás ütemezése'}
          </button>
        )}
      </div>
    </div>
  )
}

function ReferralBox({ hasSubscription }: { hasSubscription: boolean }) {
  const { data: ref } = useQuery({ queryKey: ['referral'], queryFn: () => getReferral().then(r => r.data) })
  if (!ref) return null

  async function copy() {
    try {
      await navigator.clipboard.writeText(ref.link)
      toast.success('Link kimásolva')
    } catch {
      toast.error('Nem sikerült másolni — jelöld ki és másold kézzel')
    }
  }

  return (
    <div className="rounded-xl border p-4 mb-6" style={{ borderColor: 'rgba(91,184,160,0.4)', background: 'rgba(91,184,160,0.06)' }}>
      <div className="flex items-center gap-2 font-semibold text-gray-900 text-sm mb-1">
        <Gift className="w-4 h-4" style={{ color: '#5BB8A0' }} /> Ajánlj minket — egy hónap ajándék mindkettőtöknek
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Ha egy cukrász kolléga a linkeddel regisztrál és előfizet, mindketten kaptok egy ajándék hónapot.
        {!hasSubscription && ' Az ajándék hónapot aktív előfizetéshez tudjuk hozzáadni.'}
      </p>
      <div className="flex gap-2">
        <input readOnly value={ref.link} onFocus={e => e.target.select()}
          className="input text-xs flex-1 min-w-0" />
        <button onClick={copy}
          className="shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg"
          style={{ background: '#5BB8A0', color: '#fff' }}>
          <Copy className="w-3.5 h-3.5" /> Másolás
        </button>
      </div>
      {ref.rewarded_count > 0 && (
        <p className="text-xs mt-2" style={{ color: '#3f8f7a' }}>
          Eddig {ref.rewarded_count} sikeres ajánlásod volt — köszönjük!
        </p>
      )}
    </div>
  )
}

function PlanChangeInfo() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 mb-8 text-sm text-gray-600">
      <div className="flex items-center gap-2 font-semibold text-gray-800 mb-2">
        <Info className="w-4 h-4" style={{ color: '#5BB8A0' }} /> Hogyan működik a csomagváltás?
      </div>
      <ul className="space-y-1.5 list-disc pl-5">
        <li><strong>Nagyobb csomagra vagy éves fizetésre váltás</strong> — azonnal életbe lép. A régi csomag fel nem használt napjait jóváírjuk, csak a különbözetet fizeted.</li>
        <li><strong>Kisebb csomagra vagy havi fizetésre váltás</strong> — a már kifizetett időszak végéig megmarad a jelenlegi csomagod, utána automatikusan átállunk. Visszatérítés nincs, de dupla díjat sem fizetsz.</li>
        <li><strong>Automatikus megújulás</strong> — a havi előfizetés havonta, az éves évente automatikusan megújul, és a díjat a bankkártyádról levonjuk, amíg le nem mondod.</li>
        <li><strong>Lemondás</strong> — bármikor, az „Előfizetés kezelése / lemondás” gombbal; a kifizetett időszak végéig minden elérhető marad. Utána ingyenes csomagra váltasz: a receptjeid megmaradnak, de csak az első 3 használható.</li>
      </ul>
    </div>
  )
}

export default function ElofizetsPage() {
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)
  const [billingModal, setBillingModal] = useState<string | null>(null)
  const [billingInitial, setBillingInitial] = useState<BillingForm>({ name: '', tax_number: '', address: '', email: '' })

  useEffect(() => {
    const payment = searchParams.get('payment')
    if (payment === 'success') {
      toast.success('Sikeres fizetés! Az előfizetés aktiválása folyamatban...')
      qc.invalidateQueries({ queryKey: ['subscription'] })
    } else if (payment === 'cancelled') {
      toast.error('A fizetés megszakadt.')
    }
  }, [])

  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => getSubscription().then(r => r.data),
  })

  const { data: plans = [] } = useQuery({
    queryKey: ['plans'],
    queryFn: () => getPlans().then(r => r.data),
  })

  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: () => getCompany().then(r => r.data),
  })

  const { data: stripeSub } = useQuery({
    queryKey: ['stripe-subscription'],
    queryFn: () => getStripeSubscription().then(r => r.data),
  })
  const currentKey: string | null = stripeSub?.price_key || null
  const { data: campaign } = useQuery({ queryKey: ['campaign'], queryFn: () => getCampaign().then(r => r.data) })
  const [changeModal, setChangeModal] = useState<string | null>(null)
  const [cancelPendingLoading, setCancelPendingLoading] = useState(false)

  function refreshAll() {
    setChangeModal(null)
    qc.invalidateQueries({ queryKey: ['subscription'] })
    qc.invalidateQueries({ queryKey: ['stripe-subscription'] })
  }

  async function handleCancelPending() {
    setCancelPendingLoading(true)
    try {
      await cancelPendingChange()
      toast.success('Az ütemezett váltást visszavontuk.')
      refreshAll()
    } catch {
      toast.error('Nem sikerült visszavonni a váltást')
    } finally {
      setCancelPendingLoading(false)
    }
  }

  // Aktív előfizetésnél váltás (arányosítva), egyébként új előfizetés.
  // Sikertelen terhelésnél nem engedünk új előfizetést — a függő díjat kell kifizetni (különben dupla díj).
  function handlePlanButton(price_key: string) {
    if (stripeSub?.payment_failed) {
      toast.error('Előbb kérjük, fizesd ki a függő díjat — így nem fizetsz duplán.')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (currentKey) setChangeModal(price_key)
    else handleCheckout(price_key)
  }

  async function handleCheckout(price_key: string) {
    const initial: BillingForm = {
      name:       company?.name       || '',
      tax_number: company?.tax_number || '',
      address:    company?.address    || '',
      email:      company?.email      || '',
    }
    setBillingInitial(initial)
    setBillingModal(price_key)
  }

  async function handleManageSubscription() {
    setPortalLoading(true)
    try {
      const res = await createPortalSession()
      window.location.href = res.data.portal_url
    } catch {
      toast.error('Az előfizetés-kezelés jelenleg nem elérhető ehhez a fiókhoz. Írj nekünk: info@cukraszdoc.hu')
      setPortalLoading(false)
    }
  }

  async function handleBillingConfirm(data: BillingForm) {
    if (!billingModal) return
    setCheckoutLoading(billingModal)
    try {
      await updateCompany(data)
      const res = await createCheckout(billingModal)
      window.location.href = res.data.checkout_url
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      if (detail?.code === 'payment_failed') {
        toast.error(detail.message)
        qc.invalidateQueries({ queryKey: ['stripe-subscription'] })
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Hiba a fizetési felület megnyitásakor')
      }
      setCheckoutLoading(null)
      setBillingModal(null)
    }
  }

  if (!sub) return null

  return (
    <div className="max-w-3xl">
      {billingModal && (
        <BillingModal
          onConfirm={handleBillingConfirm}
          onClose={() => setBillingModal(null)}
          loading={!!checkoutLoading}
          initial={billingInitial}
        />
      )}
      {changeModal && (
        <ChangePlanModal priceKey={changeModal} onClose={() => setChangeModal(null)} onDone={refreshAll} />
      )}

      <h1 className="text-2xl font-bold text-gray-900 mb-2">Előfizetés</h1>
      <p className="text-sm text-gray-500 mb-6">Jelenlegi csomag és bővítési lehetőségek.</p>

      {stripeSub?.payment_failed && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-800">
          <p className="font-semibold mb-1">A legutóbbi díj levonása nem sikerült ({fmtDate(stripeSub.failed_since)})</p>
          <p className="mb-3">
            A bank elutasította a kártyás terhelést; a rendszer a következő napokban automatikusan újrapróbálja.
            {sub.plan !== 'free'
              ? <> A csomagod <strong>{fmtDate(stripeSub.grace_until)}-ig</strong> változatlanul elérhető, utána ingyenesre vált, amíg a díj nincs rendezve.</>
              : <> A díj rendezése után a csomagod azonnal újra elérhető.</>}
            {' '}<strong>Kérjük, ne fizess elő újra</strong> — azzal dupla díjat fizetnél. Ehelyett fizesd ki a függő díjat, vagy frissítsd a kártyádat:
          </p>
          <div className="flex flex-wrap gap-2">
            {stripeSub.pay_url && (
              <a href={stripeSub.pay_url} className="px-4 py-2 rounded-lg font-semibold text-white" style={{ background: '#5BB8A0' }}>
                Függő díj kifizetése
              </a>
            )}
            <button onClick={handleManageSubscription} disabled={portalLoading}
              className="px-4 py-2 rounded-lg font-semibold border border-red-300 bg-white disabled:opacity-60">
              Kártya frissítése
            </button>
          </div>
        </div>
      )}

      {/* Jelenlegi csomag */}
      <div className={`rounded-xl border p-4 mb-6 ${PLAN_COLORS[sub.plan] || PLAN_COLORS.free}`}>
        <div className="flex items-center justify-between">
          <div>
            <span className="font-semibold text-base">
              {currentKey ? `${PRICE_KEY_LABELS[currentKey]} csomag`
                : sub.plan === 'free' ? 'Ingyenes csomag' : sub.plan === 'starter' ? 'Alap csomag' : 'Pro csomag'}
            </span>
            <p className="text-sm mt-0.5">
              {sub.recipe_limit
                ? `${sub.recipe_count} / ${sub.recipe_limit} recept felhasználva`
                : `${sub.recipe_count} recept (korlátlan)`}
            </p>
            {(stripeSub?.current_period_end || sub.plan_expires_at) && (
              <p className="text-xs mt-1 opacity-75">
                {stripeSub?.cancel_at ? 'Lemondva, elérhető eddig: ' : currentKey ? 'Következő megújítás: ' : 'Érvényes: '}
                {fmtDate(stripeSub?.cancel_at || stripeSub?.current_period_end || sub.plan_expires_at)}
              </p>
            )}
          </div>
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/60">Aktív</span>
        </div>
        {sub.recipe_limit && (
          <div className="mt-3 h-2 rounded-full bg-white/40 overflow-hidden">
            <div className="h-full rounded-full bg-current opacity-60 transition-all"
              style={{ width: `${Math.min(100, (sub.recipe_count / sub.recipe_limit) * 100)}%` }} />
          </div>
        )}
        {sub.plan !== 'free' && (
          <button
            onClick={handleManageSubscription}
            disabled={portalLoading}
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold underline underline-offset-2 disabled:opacity-60"
          >
            {portalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Előfizetés kezelése / lemondás
          </button>
        )}
      </div>

      <ReferralBox hasSubscription={!!currentKey} />

      {/* Ütemezett (lefelé) váltás */}
      {stripeSub?.pending_price_key && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6 text-sm text-amber-800 flex items-center justify-between gap-4">
          <span>
            <strong>{fmtDate(stripeSub.pending_at)}</strong>-tól <strong>{PRICE_KEY_LABELS[stripeSub.pending_price_key]}</strong> csomagra váltasz.
            Addig a jelenlegi csomagod minden funkciója elérhető.
          </span>
          <button onClick={handleCancelPending} disabled={cancelPendingLoading}
            className="shrink-0 text-xs font-semibold underline underline-offset-2 disabled:opacity-60">
            {cancelPendingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Visszavonás'}
          </button>
        </div>
      )}

      {/* Csomagok — Stripe-on kívül, kézzel adott Pro-nál nincs mit váltani */}
      {(sub.plan !== 'pro' || currentKey) && (
        <>
          {campaign?.active && (
            <div className="rounded-xl p-3 mb-5 text-sm font-medium" style={{ background: 'rgba(91,184,160,0.12)', color: '#1E2D27' }}>
              🎄 <strong>Karácsonyi akció:</strong> minden éves előfizetés −20% december 24-ig — havi előfizetőknek évesre váltáskor is.
              A kedvezmény automatikusan levonódik.
            </div>
          )}
          <p className="text-sm font-semibold text-gray-700 mb-4">{currentKey ? 'Csomagváltás:' : 'Bővítse előfizetését:'}</p>
          <p className="text-xs text-gray-500 -mt-2 mb-4">
            A havi előfizetés havonta, az éves évente automatikusan megújul, amíg le nem mondod. Bármikor lemondható.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 items-start">
            {(plans as any[]).map((p: any) => {
              const highlight = p.key === 'starter'
              const isCurrent = p.key === sub.plan
              return (
                <div key={p.key} className="rounded-2xl p-6 relative"
                  style={highlight ? {
                    background: '#1E2D27',
                    border: '2px solid #5BB8A0',
                    boxShadow: '0 8px 40px rgba(91,184,160,0.2)',
                  } : {
                    background: '#fff',
                    border: isCurrent ? '2px solid #5BB8A0' : '1px solid rgba(30,45,39,0.1)',
                    boxShadow: '0 2px 12px rgba(30,45,39,0.06)',
                  }}>
                  {highlight && !isCurrent && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap"
                      style={{ background: '#5BB8A0', color: '#fff' }}>
                      Legtöbbek választása
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap"
                      style={{ background: '#5BB8A0', color: '#fff' }}>
                      Jelenlegi csomag
                    </div>
                  )}
                  <p className="font-semibold text-sm mb-1" style={{ color: '#5BB8A0' }}>{p.name}</p>
                  <div className="flex items-baseline gap-1 mb-0.5">
                    <span className="text-3xl font-bold" style={{ color: highlight ? '#fff' : '#1E2D27' }}>
                      {p.price_huf === 0 ? '0 Ft' : `${p.price_huf.toLocaleString('hu')} Ft`}
                    </span>
                    {p.price_huf > 0 && (
                      <span className="text-sm" style={{ color: highlight ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>/hó + ÁFA</span>
                    )}
                  </div>
                  {p.price_huf > 0 && (
                    <p className="text-xs mb-1" style={{ color: highlight ? 'rgba(255,255,255,0.35)' : '#9ca3af' }}>
                      ({Math.round(p.price_huf * 1.27).toLocaleString('hu')} Ft/hó bruttó)
                    </p>
                  )}
                  {p.price_annual_huf && (
                    <div className="mb-4">
                      <span className="text-sm font-semibold" style={{ color: '#5BB8A0' }}>
                        {p.price_annual_huf.toLocaleString('hu')} Ft/év · 2 hónap ingyen
                      </span>
                      <p className="text-xs mt-0.5" style={{ color: highlight ? 'rgba(255,255,255,0.35)' : '#9ca3af' }}>
                        ({Math.round(p.price_annual_huf * 1.27).toLocaleString('hu')} Ft/év bruttó)
                      </p>
                    </div>
                  )}
                  <ul className="space-y-2 mb-5">
                    {p.features.map((f: string) => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: highlight ? 'rgba(91,184,160,0.2)' : 'rgba(91,184,160,0.12)' }}>
                          <Check size={10} style={{ color: '#5BB8A0' }} />
                        </div>
                        <span style={{ color: highlight ? 'rgba(255,255,255,0.8)' : '#374151' }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {p.key !== 'free' && p.stripe_monthly && (currentKey || !isCurrent) && (
                    <div className="space-y-2">
                      <button onClick={() => handlePlanButton(p.stripe_monthly)}
                        disabled={!!checkoutLoading || currentKey === p.stripe_monthly}
                        className="flex items-center justify-center gap-1.5 w-full text-sm font-semibold py-2.5 rounded-xl transition-all disabled:opacity-60"
                        style={{ background: '#5BB8A0', color: '#fff' }}>
                        {checkoutLoading === p.stripe_monthly ? <Loader2 className="w-4 h-4 animate-spin" />
                          : currentKey ? <ArrowRightLeft className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                        {currentKey === p.stripe_monthly ? 'Jelenlegi: havi' : currentKey ? 'Váltás havira' : 'Havi előfizetés'}
                      </button>
                      {p.stripe_annual && (
                        <button onClick={() => handlePlanButton(p.stripe_annual)}
                          disabled={!!checkoutLoading || currentKey === p.stripe_annual}
                          className="flex items-center justify-center gap-1.5 w-full text-sm font-semibold py-2.5 rounded-xl transition-all disabled:opacity-60"
                          style={{ background: highlight ? 'rgba(255,255,255,0.08)' : 'rgba(30,45,39,0.07)', color: highlight ? '#fff' : '#1E2D27', border: highlight ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                          {checkoutLoading === p.stripe_annual ? <Loader2 className="w-4 h-4 animate-spin" />
                            : currentKey ? <ArrowRightLeft className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                          {currentKey === p.stripe_annual ? 'Jelenlegi: éves'
                            : `${currentKey ? 'Váltás évesre' : 'Éves'} ${campaign?.active ? '(−20% akció)' : '(2 hónap ingyen)'}`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <PlanChangeInfo />
        </>
      )}

      {/* Békás Addon */}
      <p className="text-sm font-semibold text-gray-700 mb-3">Kiegészítők</p>
      <div className={`rounded-xl border p-4 ${sub.bekas_pack ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">Békás Kft. Prémium Csomag</div>
              <p className="text-xs text-gray-500 mt-0.5">182 cukrászati alapanyag tápértékkel, allergénekkel és összetétel adattal</p>
              {sub.bekas_pack ? (
                <div className="mt-1.5">
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">Aktív</span>
                  {sub.bekas_pack_expires_at && (
                    <span className="text-xs text-gray-400 ml-2">
                      Megújul: {new Date(sub.bekas_pack_expires_at).toLocaleDateString('hu')}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-xs text-amber-600 font-semibold mt-1">14 900 Ft + ÁFA / év</p>
                  <p className="text-xs text-gray-400 mt-0.5">(18 923 Ft / év bruttó)</p>
                </>
              )}
            </div>
          </div>
          {!sub.bekas_pack && (
            <button
              onClick={() => handleCheckout('bekas_annual')}
              disabled={!!checkoutLoading}
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
            >
              {checkoutLoading === 'bekas_annual' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
              Megvásárlás
            </button>
          )}
        </div>
      </div>

      {/* Számla-fotó tárolás Addon */}
      <div className={`rounded-xl border p-4 mt-3 ${sub.invoice_photos_addon ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Camera className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">Számla-fotó tárolás</div>
              <p className="text-xs text-gray-500 mt-0.5">Fotózd le a beszállítói számlát minden alapanyag-tételhez a Nyomonkövetés menüben</p>
              {sub.invoice_photos_addon ? (
                <div className="mt-1.5">
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">Aktív</span>
                  {sub.invoice_photos_addon_expires_at && (
                    <span className="text-xs text-gray-400 ml-2">
                      Megújul: {new Date(sub.invoice_photos_addon_expires_at).toLocaleDateString('hu')}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-xs text-amber-600 font-semibold mt-1">990 Ft + ÁFA / hó</p>
                  <p className="text-xs text-gray-400 mt-0.5">(1 257 Ft / hó bruttó)</p>
                </>
              )}
            </div>
          </div>
          {!sub.invoice_photos_addon && (
            <button
              onClick={() => handleCheckout('invoice_photos_monthly')}
              disabled={!!checkoutLoading}
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
            >
              {checkoutLoading === 'invoice_photos_monthly' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
              Megvásárlás
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
